import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import BuildIcon from '@mui/icons-material/Build';
import EventIcon from '@mui/icons-material/Event';
import BadgeIcon from '@mui/icons-material/Badge';

import Modal from '@components/Modal/Modal';
import { StatusChip } from '@components/StatusChip';
import { OrderDetailStatus } from '@api/enterprise/openapi/models';
import { useMasterScriptsQuery } from '@features/scripts/hooks/useScripts';
import { formatPositionLabel } from '../../utils';

import type { SelectChangeEvent } from '@mui/material';
import type { RackCell } from '../../types';
import type { Script } from '@features/scripts/types';
import type { WorkOrderDetailViewModel } from '@api/enterprise/openapi/models';

type Props = {
  open: boolean;
  onClose: () => void;
  cell: RackCell | null;
  onDetailClick?: (globalId: string) => void;
  onSkipClick?: (detail: WorkOrderDetailViewModel) => void;
  onUnskipClick?: (detail: WorkOrderDetailViewModel) => void;
  onOverride?: (sequenceNumber: number, script: Script) => void;
  isReadOnly?: boolean;
};

const CellDetailModal: React.FC<Props> = ({
  open,
  onClose,
  cell,
  onDetailClick,
  onSkipClick,
  onUnskipClick,
  onOverride,
  isReadOnly = false
}) => {
  const { t } = useTranslation('workOrderDetails');
  const detail = cell?.detail ?? null;
  const resolution = cell?.resolution ?? null;

  const { data: scriptsResponse } = useMasterScriptsQuery();
  const masterScripts = useMemo(
    () => scriptsResponse?.data ?? [],
    [scriptsResponse]
  );

  const handleScriptChange = useCallback(
    (event: SelectChangeEvent) => {
      if (!cell) return;
      const script = masterScripts.find(s => s.id === event.target.value);
      if (script) onOverride?.(cell.sequenceNumber, script);
    },
    [cell, masterScripts, onOverride]
  );

  const title = useMemo(() => {
    if (!cell) return '';
    const posLabel = formatPositionLabel(cell.sequenceNumber);
    const assetId = detail?.id || t('equipment.noAssetId');
    return `${posLabel} — ${assetId}`;
  }, [cell, detail, t]);

  const isSkipped = detail?.status === OrderDetailStatus.Skipped;
  const isComplete = detail?.status === OrderDetailStatus.Complete;
  const isIncomplete = detail?.status === OrderDetailStatus.Incomplete;

  const handlePrimaryClick = useCallback(() => {
    if (!detail?.globalId) return;
    onDetailClick?.(detail.globalId);
    onClose();
  }, [detail, onDetailClick, onClose]);

  const handleSkipClick = useCallback(() => {
    if (!detail) return;
    onSkipClick?.(detail);
    onClose();
  }, [detail, onSkipClick, onClose]);

  const handleUnskipClick = useCallback(() => {
    if (!detail) return;
    onUnskipClick?.(detail);
    onClose();
  }, [detail, onUnskipClick, onClose]);

  const primaryButton = useMemo(() => {
    if (!detail) return undefined;

    if (isComplete) {
      return {
        text: t('rack.viewResults'),
        onClick: handlePrimaryClick
      };
    }

    if (isSkipped) {
      return {
        text: t('rack.viewDetails'),
        onClick: handlePrimaryClick
      };
    }

    return {
      text: t('rack.startCalibration'),
      onClick: handlePrimaryClick
    };
  }, [detail, isComplete, isSkipped, t, handlePrimaryClick]);

  const tertiaryButton = useMemo(() => {
    if (!detail || isReadOnly || isComplete) return undefined;

    if (isSkipped) {
      return {
        text: t('rack.resumeEquipment'),
        onClick: handleUnskipClick,
        variant: 'text' as const
      };
    }

    if (isIncomplete) {
      return {
        text: t('rack.skipEquipment'),
        onClick: handleSkipClick,
        variant: 'text' as const
      };
    }

    return undefined;
  }, [
    detail,
    isReadOnly,
    isComplete,
    isSkipped,
    isIncomplete,
    t,
    handleUnskipClick,
    handleSkipClick
  ]);

  return (
    <Modal open={open} onClose={onClose} width="480px">
      <Modal.Header title={title} onClose={onClose} />
      <Modal.Content>
        {!detail ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              {t('rack.noEquipment')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <DetailField
              icon={
                <BadgeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              }
              label={t('columns.id')}
              value={detail.id}
            />
            <DetailField
              icon={
                <BusinessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              }
              label={t('columns.company')}
              value={detail.companyName}
            />
            <DetailField
              icon={
                <BuildIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              }
              label={t('columns.eventType')}
              value={detail.relatedEventType}
            />
            <DetailField
              label={t('columns.description')}
              value={detail.description}
            />
            <DetailField
              icon={
                <EventIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              }
              label={t('columns.scheduledDate')}
              value={detail.scheduledDate}
            />
            <DetailField
              label={t('columns.status')}
              value={
                <StatusChip
                  label={detail.status}
                  variant="status"
                  size="small"
                />
              }
            />

            <Divider sx={{ my: 0.5 }} />

            <Typography variant="subtitle2" color="text.secondary">
              {t('rack.scriptSection')}
            </Typography>

            <DetailField
              label={t('equipment.manufacturer')}
              value={resolution?.equipment?.manufacturer}
            />
            <DetailField
              label={t('equipment.model')}
              value={resolution?.equipment?.modelNumber}
            />
            <DetailField
              label={t('equipment.calibrationProcedure')}
              value={resolution?.equipment?.procedureName}
            />
            <DetailField
              label={t('rack.resolvedScript')}
              value={
                resolution?.resolvedScript ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {resolution.resolvedScript.globalId}
                    </Typography>
                    <Chip
                      label={
                        resolution.overridden
                          ? t('rack.overridden')
                          : t('rack.autoResolved')
                      }
                      size="small"
                      color={resolution.overridden ? 'info' : 'success'}
                      variant="outlined"
                    />
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    color="warning.main"
                    fontWeight={500}
                  >
                    {t('rack.unresolvedScript')}
                  </Typography>
                )
              }
            />

            {!isReadOnly && (
              <FormControl size="small" fullWidth>
                <InputLabel id="rack-override-script-label">
                  {t('rack.overrideScript')}
                </InputLabel>
                <Select
                  labelId="rack-override-script-label"
                  label={t('rack.overrideScript')}
                  value={resolution?.resolvedScript?.id ?? ''}
                  onChange={handleScriptChange}
                >
                  {masterScripts.map(script => (
                    <MenuItem key={script.id} value={script.id}>
                      {script.globalId}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        )}
      </Modal.Content>
      {detail && primaryButton && (
        <Modal.Footer
          primaryButton={primaryButton}
          tertiaryButton={tertiaryButton}
        />
      )}
    </Modal>
  );
};

type DetailFieldProps = {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
};

const DetailField: React.FC<DetailFieldProps> = ({ icon, label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    {icon}
    <Typography variant="body2" color="text.secondary">
      {label}:
    </Typography>
    {typeof value === 'string' || value == null ? (
      <Typography variant="body2" fontWeight={500}>
        {value || '—'}
      </Typography>
    ) : (
      value
    )}
  </Box>
);

export default CellDetailModal;
